/**
 * MultipleChoice.tsx
 * Task pane panel for configuring a Multiple Choice activity on the current slide.
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Check, Timer, Star, Shuffle, Zap } from 'lucide-react';
import { clsx } from 'clsx';
import Button from '@components/shared/Button';
import { useAppStore } from '@store/appStore';
import { saveSlideConfig, loadSlideConfig } from '@office/settingsService';
import { injectActivityShape, buildShapeDescriptor, getCurrentSlideIndex } from '@office/slideService';
import type { MCConfig, MCOption } from '@types-local/quiz.types';

function generateOptionId() {
  return `opt_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
}

const DEFAULT_OPTIONS: MCOption[] = [
  { id: generateOptionId(), text: '', isCorrect: false },
  { id: generateOptionId(), text: '', isCorrect: false },
  { id: generateOptionId(), text: '', isCorrect: false },
  { id: generateOptionId(), text: '', isCorrect: false },
];

export default function MultipleChoice(): React.ReactElement {
  const [question, setQuestion] = useState('');
  const [options, setOptions] = useState<MCOption[]>(DEFAULT_OPTIONS);
  const [timeLimit, setTimeLimit] = useState<number>(30);
  const [timeLimitEnabled, setTimeLimitEnabled] = useState(false);
  const [competitionMode, setCompetitionMode] = useState(false);
  const [starValue, setStarValue] = useState(1);
  const [shuffle, setShuffle] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const isOfficeReady = useAppStore((s) => s.isOfficeReady);

  // Repopulate the form from the current slide's saved config, so
  // returning to a slide that already has a question shows it instead
  // of a blank form.
  useEffect(() => {
    if (!isOfficeReady) return;
    let cancelled = false;
    (async () => {
      const slideIndex = await getCurrentSlideIndex();
      const cfg = loadSlideConfig(slideIndex);
      if (cancelled || !cfg || cfg.type !== 'multiple_choice') return;
      setQuestion(cfg.question);
      setOptions(cfg.options);
      setTimeLimitEnabled(cfg.timeLimit !== null);
      if (cfg.timeLimit !== null) setTimeLimit(cfg.timeLimit);
      setCompetitionMode(cfg.competitionMode);
      setStarValue(cfg.starValue);
      setShuffle(cfg.shuffleOptions);
    })();
    return () => { cancelled = true; };
  }, [isOfficeReady]);

  const setCorrect = (id: string) => {
    setOptions((prev) => prev.map((o) => ({ ...o, isCorrect: o.id === id })));
  };

  const updateOptionText = (id: string, text: string) => {
    setOptions((prev) => prev.map((o) => (o.id === id ? { ...o, text } : o)));
  };

  const addOption = () => {
    if (options.length >= 6) return;
    setOptions((prev) => [...prev, { id: generateOptionId(), text: '', isCorrect: false }]);
  };

  const removeOption = (id: string) => {
    if (options.length <= 2) return;
    setOptions((prev) => prev.filter((o) => o.id !== id));
  };

  const handleSave = async () => {
    const hasCorrect = options.some((o) => o.isCorrect);
    if (!question.trim()) { setError('Add a question first.'); return; }
    if (!hasCorrect) { setError('Click a letter circle to mark the correct answer.'); return; }

    setError('');
    setSaving(true);
    try {
      const config: MCConfig = {
        type: 'multiple_choice',
        question: question.trim(),
        options,
        timeLimit: timeLimitEnabled ? timeLimit : null,
        competitionMode,
        starValue,
        shuffleOptions: shuffle,
        showCorrectAnswerAfter: true,
        blooms: null,
      };

      const slideIndex = await getCurrentSlideIndex();
      const shapeName = `IQ_QUIZ_MC_${slideIndex.toString().padStart(2, '0')}`;
      const descriptor = buildShapeDescriptor(config, shapeName);

      await saveSlideConfig(slideIndex, config, shapeName);
      await injectActivityShape(descriptor, slideIndex);

      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      console.error('[MC] Save failed:', err);
      setError(err instanceof Error ? err.message : 'Could not add to the slide. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const OPTION_LABELS = ['A', 'B', 'C', 'D', 'E', 'F'];

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-xl bg-brand-500/20 border border-brand-500/30 flex items-center justify-center text-base">📊</div>
        <div>
          <h2 className="text-sm font-bold text-slate-900">Multiple Choice</h2>
          <p className="text-[10px] text-slate-500">Students select one answer on their device</p>
        </div>
      </div>

      {/* Question input */}
      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1.5">Question</label>
        <textarea
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          placeholder="What is the capital of the Philippines?"
          rows={3}
          className="w-full bg-surface-elevated border border-surface-border rounded-xl px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 resize-none focus:outline-none focus:border-brand-500 transition-colors"
        />
      </div>

      {/* Options */}
      <div>
        <label className="block text-xs font-semibold text-slate-600 mb-1.5">
          Answer Options <span className="text-slate-400 font-normal">(click circle to mark correct)</span>
        </label>
        <div className="space-y-2">
          <AnimatePresence>
            {options.map((opt, i) => (
              <motion.div
                key={opt.id}
                layout
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 8, height: 0 }}
                className="flex items-center gap-2"
              >
                {/* Correct answer indicator */}
                <button
                  onClick={() => setCorrect(opt.id)}
                  className={clsx(
                    'flex-shrink-0 w-7 h-7 rounded-full border-2 flex items-center justify-center text-xs font-bold transition-all',
                    opt.isCorrect
                      ? 'bg-green-500 border-green-400 text-white'
                      : 'border-surface-border text-slate-500 hover:border-slate-400',
                  )}
                  title={opt.isCorrect ? 'Correct answer' : 'Mark as correct'}
                >
                  {opt.isCorrect ? <Check size={12} /> : OPTION_LABELS[i]}
                </button>

                {/* Text input */}
                <input
                  type="text"
                  value={opt.text}
                  onChange={(e) => updateOptionText(opt.id, e.target.value)}
                  placeholder={`Option ${OPTION_LABELS[i]}`}
                  className={clsx(
                    'flex-1 bg-surface-elevated border rounded-xl px-3 py-2 text-sm text-slate-900 placeholder-slate-400',
                    'focus:outline-none transition-colors',
                    opt.isCorrect ? 'border-green-500/50 bg-green-500/10' : 'border-surface-border focus:border-brand-500',
                  )}
                />

                {/* Remove button */}
                {options.length > 2 && (
                  <button
                    onClick={() => removeOption(opt.id)}
                    className="flex-shrink-0 p-1.5 text-slate-400 hover:text-red-400 transition-colors rounded-lg hover:bg-red-400/10"
                  >
                    <Trash2 size={13} />
                  </button>
                )}
              </motion.div>
            ))}
          </AnimatePresence>

          {options.length < 6 && (
            <button
              onClick={addOption}
              className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-brand-300 transition-colors py-1 pl-9"
            >
              <Plus size={12} /> Add option
            </button>
          )}
        </div>
      </div>

      {/* Settings */}
      <div className="space-y-2.5 pt-1">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Settings</p>

        {/* Timer */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Timer size={13} className="text-slate-500" />
            <span className="text-xs text-slate-700">Time Limit</span>
          </div>
          <div className="flex items-center gap-2">
            {timeLimitEnabled && (
              <input
                type="number"
                min={5}
                max={300}
                value={timeLimit}
                onChange={(e) => setTimeLimit(Number(e.target.value))}
                className="w-14 bg-surface-elevated border border-surface-border rounded-lg px-2 py-1 text-xs text-slate-900 text-center focus:outline-none focus:border-brand-500"
              />
            )}
            <button
              onClick={() => setTimeLimitEnabled((v) => !v)}
              className={clsx(
                'w-10 h-5 rounded-full transition-all',
                timeLimitEnabled ? 'bg-brand-500' : 'bg-surface-elevated border border-surface-border',
              )}
            >
              <div className={clsx('w-3.5 h-3.5 rounded-full bg-white mx-auto transition-transform', timeLimitEnabled ? 'translate-x-2.5' : '-translate-x-2.5')} />
            </button>
          </div>
        </div>

        {/* Competition mode */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap size={13} className="text-yellow-500" />
            <span className="text-xs text-slate-700">Competition Mode</span>
          </div>
          <button
            onClick={() => setCompetitionMode((v) => !v)}
            className={clsx('w-10 h-5 rounded-full transition-all', competitionMode ? 'bg-yellow-500' : 'bg-surface-elevated border border-surface-border')}
          >
            <div className={clsx('w-3.5 h-3.5 rounded-full bg-white mx-auto transition-transform', competitionMode ? 'translate-x-2.5' : '-translate-x-2.5')} />
          </button>
        </div>

        {/* Star value */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Star size={13} className="text-yellow-400" />
            <span className="text-xs text-slate-700">Stars on Correct</span>
          </div>
          <div className="flex items-center gap-1">
            {[1, 2, 3].map((v) => (
              <button
                key={v}
                onClick={() => setStarValue(v)}
                className={clsx('w-7 h-7 rounded-lg text-xs font-bold transition-all', starValue === v ? 'bg-yellow-500 text-white' : 'bg-surface-elevated text-slate-500 hover:text-slate-800')}
              >
                {v}⭐
              </button>
            ))}
          </div>
        </div>

        {/* Shuffle */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shuffle size={13} className="text-slate-500" />
            <span className="text-xs text-slate-700">Shuffle Options</span>
          </div>
          <button
            onClick={() => setShuffle((v) => !v)}
            className={clsx('w-10 h-5 rounded-full transition-all', shuffle ? 'bg-brand-500' : 'bg-surface-elevated border border-surface-border')}
          >
            <div className={clsx('w-3.5 h-3.5 rounded-full bg-white mx-auto transition-transform', shuffle ? 'translate-x-2.5' : '-translate-x-2.5')} />
          </button>
        </div>
      </div>

      {error && (
        <p className="text-xs text-red-500 bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2">{error}</p>
      )}

      {/* Save CTA. Kept clickable (except while Office is loading) so the
          teacher gets a reason if a required field is missing, instead of a
          dead-looking button. */}
      <Button
        fullWidth
        size="md"
        onClick={handleSave}
        loading={saving}
        disabled={!isOfficeReady}
        variant={saved ? 'secondary' : 'primary'}
        icon={saved ? <Check size={14} /> : undefined}
      >
        {saved ? 'Saved to Slide!' : 'Add to Slide'}
      </Button>
    </div>
  );
}
